import { type PropsWithChildren, type ReactNode } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { Footer } from '@/components/global/Footer';
import { GlobalStyle } from '@/components/v2/GlobalStyle';
import { ContentMarginLine, GridRule } from '@/components/v2/GridLines';
import { Header } from '@/components/v2/Header';
import { HeaderUIProvider } from '@/components/v2/Header/HeaderUIProvider';
import { UtilityCss } from '@/components/v2/UtilityCss';
import { styles } from '@/components/v2/UtilityCss/Text';
import { useHasMounted } from '@/hooks/useHasMounted';

import { FontLinks } from './FontLinks';
import { LayoutGrid, LayoutSubgrid } from './LayoutGrid';

const PaperTexture = styled(LayoutSubgrid)`
  background-image: ${({ theme }) => theme.paperTextureCssVars.img};
  background-repeat: repeat;
  background-size: ${({ theme }) => theme.paperTextureCssVars.size};
  mix-blend-mode: ${({ theme }) => theme.paperTextureCssVars.mixBlendMode};
  opacity: ${({ theme }) => theme.paperTextureCssVars.opacity};
  pointer-events: none;
`;

export default function Page({
  children,
  seo
}: PropsWithChildren<{ seo: ReactNode }>) {
  const hasMounted = useHasMounted();

  return (
    <>
      <FontLinks />
      <GlobalStyle />
      <UtilityCss />
      {seo}
      <HeaderUIProvider>
        <LayoutGrid className={styles.base}>
          <a className="sr-only" href="#main">
            Skip to main content
          </a>

          <PaperTexture data-pile />

          <LayoutSubgrid data-pile>
            <Header />

            <LayoutSubgrid as="main" id="main">
              {children}
            </LayoutSubgrid>

            <LayoutSubgrid>
              <ContentMarginLine data-bottom data-top />
              <Footer v2Colors />
              <ContentMarginLine data-bottom data-right data-top />
            </LayoutSubgrid>

            <LayoutSubgrid style={{ height: '2rem' }}>
              <ContentMarginLine data-minor />
              <GridRule $column={4} />
              <GridRule $column={10} />
              <ContentMarginLine data-minor data-right />
            </LayoutSubgrid>
          </LayoutSubgrid>
        </LayoutGrid>

        {hasMounted ? (
          <Tooltip className="toolTip" clickable={true} id="tooltip" />
        ) : null}
      </HeaderUIProvider>
    </>
  );
}
